import equal from 'fast-deep-equal';

const isContainer = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const MATCH = Symbol('match');

/** The part of `value` that differs from `other`, or MATCH when none does. */
const prune = (value: unknown, other: unknown): unknown => {
  if (equal(value, other)) {
    return MATCH;
  }

  if (!isContainer(value) || !isContainer(other)) {
    return value;
  }

  // Arrays keep only the differing indices, keyed by index, since dropping the
  // matching ones leaves nothing to say which was which.
  const keys = Array.isArray(value)
    ? value.map((_, index) => String(index))
    : Object.keys(value);
  const pruned: Record<string, unknown> = {};

  keys.forEach((key) => {
    const part = prune(value[key], other[key]);
    if (part !== MATCH) {
      pruned[key] = part;
    }
  });

  return pruned;
};

/**
 * The two states cut down to the fields that differ, with the parents that
 * lead to them: every branch that matches on both sides is dropped. A field
 * only one side has appears on that side alone.
 */
export const differingFields = (
  left: unknown,
  right: unknown,
): [unknown, unknown] => [
  prune(left, right) === MATCH ? {} : prune(left, right),
  prune(right, left) === MATCH ? {} : prune(right, left),
];
